import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { 
  Box, 
  Typography, 
  Grid2 as Grid, 
  TextField, 
  Button, 
  Select, 
  FormControl, 
  InputLabel, 
  useMediaQuery,
  useTheme
} from '@mui/material';
import emailjs from "emailjs-com";

type Option = { value: string; label: string; };

const options: Option[] = [
  { value: '0', label: 'ご協業' },
  { value: '1', label: '採用に関して' },
  { value: '2', label: 'フリーランス' },
  { value: '3', label: 'その他' },
];

const Contact = () => {
  const { control, handleSubmit, formState: { errors } } = useForm();
  const [selectedValue, setSelectedValue] = useState<string>('');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // 👈 iPhone SEなどの「背の低い」デバイスを判定
  const isShortScreen = useMediaQuery('(max-height:700px)');

  const onSubmit = (data: any) => {
    const serviceID = 'service_sgw5afv'; 
    const templateID = 'template_2q8r9p8'; 
    const userID = 'zBeYHVRlXq3Nc_bE5';
    const selectedLabel = options.find(option => option.value === selectedValue)?.label || '';
    
    emailjs.send(serviceID, templateID, { ...data, area: selectedLabel }, userID)
      .then(() => alert('送信が完了しました！'))
      .catch(() => alert('送信に失敗しました。'));
  };

  const inputStyle = {
    // 👈 スマホで高さが厳しい時はマージンをさらに詰め、PCではゆったり
    mb: { xs: 1.2, md: 2.5 }, 
    '& .MuiInputBase-root': { fontSize: { xs: '13px', md: '16px' } },
    '& .MuiInputLabel-root': { fontSize: { xs: '13px', md: '16px' } },
  };

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      pt: { xs: '70px', md: '40px' }, // 上の余白を少しだけ削る
      pb: { xs: '20px', md: '40px' }, // 下の余白も削る
      boxSizing: 'border-box',
    }}>
      
      <Box sx={{ 
        width: { xs: '92%', md: '900px' },
        my: 'auto', 
        display: 'flex',
        flexDirection: 'column',
      }}>
        
        <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 6 } }}>
          <Typography variant="h4" sx={{ 
            fontSize: { xs: '26px', md: '40px' }, // スマホタイトルを少し小さく
            fontWeight: 'bold', 
            fontFamily: "'Noto Serif JP', serif" 
          }}>
            Contact
          </Typography>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontFamily: "'Noto Serif JP', serif",
              fontSize: { xs: "1.2rem", md: "1.5rem" } 
            }}
          >
            お問い合わせ
          </Typography>
        </Box>

        <Box component="form" sx={{ width: '100%' }}>
          <Grid container spacing={isMobile ? 0 : 4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Controller
                  name="area"
                  control={control}
                  defaultValue=""
                  rules={{ required: '必須項目です' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.area} sx={inputStyle} size="small">
                      <InputLabel htmlFor="area-native-simple">問い合わせ種別</InputLabel>
                      <Select
                        native
                        {...field}
                        label="問い合わせ種別"
                        inputProps={{ name: 'area', id: 'area-native-simple' }}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          setSelectedValue(e.target.value as string);
                        }}
                      >
                        <option aria-label="None" value="" />
                        {options.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />

                {[
                  { name: 'textBox1', label: 'お名前', req: "必須" },
                  { name: 'textBox2', label: 'ふりがな', req: "必須" },
                  { name: 'textBox3', label: '会社名' },
                  { name: 'textBox4', label: 'Eメール', req: "必須" },
                  { name: 'textBox5', label: '電話番号' },
                ].map((item) => (
                  <Controller
                    key={item.name}
                    name={item.name}
                    control={control}
                    defaultValue=""
                    rules={item.req ? { required: item.req } : {}}
                    render={({ field, fieldState }) => (
                      <TextField 
                        {...field} 
                        label={item.label} 
                        fullWidth 
                        size="small"
                        error={!!fieldState?.error} 
                        helperText={fieldState?.error?.message} 
                        sx={inputStyle} 
                      />
                    )}
                  />
                ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Controller
                  name="textArea"
                  control={control}
                  defaultValue=""
                  rules={{ required: '必須項目です' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      multiline
                      fullWidth
                      // 👈 短い画面（SEなど）では 4行、通常のスマホは 6行、PCは 13.5行
                      rows={isMobile ? (isShortScreen ? 4 : 6) : 13.5} 
                      label="お問い合わせ内容"
                      error={!!fieldState?.error}
                      sx={inputStyle}
                    />
                  )}
                />

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit(onSubmit)}
                  sx={{
                    py: 1.2,
                    fontWeight: 'bold',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#333' },
                    height: '44px', // 少しだけ高さを削る
                    mt: { xs: 1, md: 'auto' }, 
                    mb: { xs: 1, md: 0 } 
                  }}
                >
                  送信する
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default Contact;
